import { useCallback, useEffect, useRef, useState } from 'react'
import {
  base64ToBytes,
  bytesToBase64,
  float32ToPcm16,
  pcm16ToFloat32,
  resample,
} from './pcm'
import { initialVad, rms, step, type VadState } from './vad'

/**
 * The microphone and the speaker, with no opinion about where the audio goes.
 *
 * Transport-agnostic on purpose: it takes an `onChunk` for the captured
 * audio and exposes `play` for the received audio, so the support socket and
 * any future one wire into the same thing. The arithmetic lives in `pcm.ts`.
 *
 * CAPTURE runs in an AudioWorklet rather than a ScriptProcessor, because a
 * ScriptProcessor runs on the main thread and a React re-render mid-utterance
 * drops samples. The worklet is defined as a string and loaded from a blob
 * URL: it has to be a separate module and we are not shipping a second
 * bundle entry for forty lines.
 *
 * PLAYBACK IS A QUEUE, not a per-chunk `decodeAudioData`. Chunks arrive
 * faster than they play, and scheduling each one as it lands produces
 * overlapping sources, which sounds like two people reading the same
 * sentence slightly out of step.
 */

const WORKLET = `
class PCMProcessor extends AudioWorkletProcessor {
  constructor() {
    super()
    this.size = 2048
    this.buffer = new Float32Array(this.size)
    this.at = 0
  }
  process(inputs) {
    const input = inputs[0]
    if (!input || !input[0]) return true
    const channel = input[0]
    for (let i = 0; i < channel.length; i++) {
      this.buffer[this.at++] = channel[i]
      if (this.at >= this.size) {
        this.port.postMessage(this.buffer.slice(0))
        this.at = 0
      }
    }
    return true
  }
}
registerProcessor('pcm-processor', PCMProcessor)
`

/** The jitter buffer, in milliseconds. See the note on `play`. */
const LEAD_MS = 120

/**
 * How long the agent counts as still speaking after its last sound.
 *
 * A reply arrives as a stream of sentences, so between two of them there
 * is a moment with nothing scheduled. Clearing "speaking" there dropped
 * the bar to interrupt from six times the room to three and a half, for
 * the exact moment the speakers were still ringing and the microphone was
 * hearing the end of the agent's own sentence. That opened a turn, cut the
 * agent off mid-reply, and sent an utterance of echo to be transcribed.
 *
 * It is all over the production logs as turns that transcribe to nothing,
 * stacked up between "Hello?" and "Still there?".
 *
 * 450ms covers the gap between sentences and the tail of a room without
 * noticeably delaying a real interruption, which still only needs to clear
 * the higher bar it was always meant to clear.
 */
const SPEAKING_HANGOVER_MS = 450

/**
 * How many captured frames to keep as pre-roll.
 *
 * A frame is 2048 samples, so about 128ms at 16kHz.
 *
 * EIGHT, RAISED FROM FOUR, because four was arithmetic rather than
 * measurement and it was clipping the front off short sentences. The window
 * is taken at the moment speech is CONFIRMED, which is at least 180ms after
 * the level first rose and quantised to a whole frame on top, so four
 * frames left barely 250ms of genuine lead-in. A word that ramps up rather
 * than starting on a plosive never crosses the threshold until it is
 * already underway, so that lead-in is the only thing holding its first
 * syllable.
 *
 * It showed up in production as transcripts that were not English:
 * "Eleventh of October" came back as "Yllefed o octobar" at 1.92s and
 * correctly at 2.56s, and short turns landed as "Hej", "Прван" and
 * "There are types". The agent was answering mangled input, which from the
 * outside is an agent that cannot understand you. A second of lead-in costs
 * nothing: it is sent only when somebody actually spoke.
 */
const PREROLL_FRAMES = 8

export type PcmState = 'off' | 'starting' | 'live' | 'error'

export interface PcmVoice {
  state: PcmState
  /** True while the far end's audio is actually coming out of the speaker. */
  speaking: boolean
  muted: boolean
  /** For a visualiser: the microphone and the playback. */
  inputAnalyser: React.RefObject<AnalyserNode | null>
  outputAnalyser: React.RefObject<AnalyserNode | null>
  start: () => Promise<void>
  stop: () => void
  /** One chunk of received audio, base64 PCM16 at `sampleRate`. */
  play: (base64: string) => void
  /** Barge-in: drop everything buffered and stop the current source dead. */
  flush: () => void
  toggleMute: () => void
}

export function usePcmVoice({
  sampleRate = 16000,
  onChunk,
  onUtterance,
  onSpeechStart,
  /**
   * `provider` streams every chunk and lets the far end decide when a turn
   * ended. `client` keeps the chunks here, watches the level, and sends one
   * complete utterance once the person stops. See `vad.ts`.
   */
  turnDetection = 'provider',
}: {
  sampleRate?: number
  /** Captured microphone audio, base64 PCM16. Only in `provider` mode. */
  onChunk: (base64: string) => void
  /** One complete utterance, base64 PCM16. Only in `client` mode. */
  onUtterance?: (base64: string) => void
  /** They started talking, so anything being said can be cut off. */
  onSpeechStart?: () => void
  turnDetection?: 'provider' | 'client'
}): PcmVoice {
  const [state, setState] = useState<PcmState>('off')
  const [speaking, setSpeaking] = useState(false)
  const [muted, setMuted] = useState(false)

  const capture = useRef<AudioContext | null>(null)
  const stream = useRef<MediaStream | null>(null)
  const playback = useRef<AudioContext | null>(null)
  const inputAnalyser = useRef<AnalyserNode | null>(null)
  const outputAnalyser = useRef<AnalyserNode | null>(null)
  const gain = useRef<GainNode | null>(null)
  /** Sources already scheduled, so a barge-in can stop every one of them. */
  const live = useRef(new Set<AudioBufferSourceNode>())
  /** The audio-clock time the next chunk should start at. */
  const nextAt = useRef(0)
  const mutedRef = useRef(false)
  // Read through a ref so a caller passing a fresh closure every render does
  // not tear the microphone down and build it again.
  const sink = useRef(onChunk)
  sink.current = onChunk
  const utteranceSink = useRef(onUtterance)
  utteranceSink.current = onUtterance
  const speechStart = useRef(onSpeechStart)
  speechStart.current = onSpeechStart
  const mode = useRef(turnDetection)
  mode.current = turnDetection
  /** The utterance being collected, in `client` mode. */
  const pending = useRef<Float32Array[]>([])
  const vad = useRef<VadState>(initialVad())
  /**
   * The last few frames, always.
   *
   * The detector deliberately waits `onsetMs` before believing a sound is
   * speech, so by the time a turn opens the first syllable has already
   * been and gone. Keeping a short rolling window means nothing is lost:
   * "hello" reaches transcription as "hello" rather than "ello".
   */
  const preroll = useRef<Float32Array[]>([])
  /** Whether the agent is talking, which raises the bar to interrupt. */
  const speakingRef = useRef(false)
  /** The pending end-of-speech timer. See SPEAKING_HANGOVER_MS. */
  const hangover = useRef<number | null>(null)

  const ensurePlayback = useCallback((): AudioContext => {
    if (!playback.current) {
      const ctx = new AudioContext()
      const g = ctx.createGain()
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 256
      analyser.smoothingTimeConstant = 0.4
      g.connect(analyser)
      analyser.connect(ctx.destination)
      playback.current = ctx
      gain.current = g
      outputAnalyser.current = analyser
    }
    return playback.current
  }, [])

  /**
   * SCHEDULED ON THE AUDIO CLOCK, not chained on `onended`.
   *
   * Chaining each chunk off the previous one's `onended` puts a main-thread
   * hop between every buffer, and the gap is audible: with speech arriving
   * in chunks of a few tens of milliseconds it is a click at every seam,
   * which adds up to a continuous crackle. Scheduling each buffer at an
   * absolute time instead is sample-accurate and gapless, because the audio
   * thread already knows when the next one starts.
   *
   * LEAD_MS is the jitter buffer. Scheduling at `currentTime` exactly means
   * a chunk arriving a millisecond late is scheduled in the past and plays
   * immediately, which is another gap; a small lead absorbs ordinary
   * network jitter at the cost of being that far behind live.
   */
  const clearHangover = useCallback(() => {
    if (hangover.current === null) return
    window.clearTimeout(hangover.current)
    hangover.current = null
  }, [])

  const play = useCallback(
    (base64: string) => {
      /**
       * MUTE IS BOTH DIRECTIONS.
       *
       * It stopped the microphone only, so pressing it while the agent was
       * mid-sentence left it talking at somebody who had just asked it to
       * be quiet. One button on the microphone has one obvious meaning,
       * and it is "stop", not "stop listening but keep going".
       *
       * Dropped here as well as flushed on the press, because the server
       * has usually already sent several seconds of audio that would
       * otherwise schedule itself straight after the flush.
       */
      if (mutedRef.current) return
      const ctx = ensurePlayback()
      if (ctx.state === 'suspended') void ctx.resume()
      const samples = resample(
        pcm16ToFloat32(base64ToBytes(base64)),
        sampleRate,
        ctx.sampleRate
      )
      if (samples.length === 0) return
      const buffer = ctx.createBuffer(1, samples.length, ctx.sampleRate)
      buffer.getChannelData(0).set(samples)
      const source = ctx.createBufferSource()
      source.buffer = buffer
      source.connect(gain.current ?? ctx.destination)

      const earliest = ctx.currentTime + LEAD_MS / 1000
      const at = Math.max(earliest, nextAt.current)
      source.start(at)
      nextAt.current = at + buffer.duration

      live.current.add(source)
      source.onended = () => {
        live.current.delete(source)
        if (live.current.size > 0) return
        // Not yet: the next sentence of the same reply is probably still in
        // flight. See SPEAKING_HANGOVER_MS.
        hangover.current = window.setTimeout(() => {
          hangover.current = null
          if (live.current.size > 0) return
          speakingRef.current = false
          setSpeaking(false)
        }, SPEAKING_HANGOVER_MS)
      }
      // A ref as well as state: the capture callback runs far more often
      // than React renders and needs the current answer, not last render's.
      clearHangover()
      speakingRef.current = true
      setSpeaking(true)
    },
    [ensurePlayback, sampleRate, clearHangover]
  )

  const flush = useCallback(() => {
    // Everything ALREADY SCHEDULED has to be stopped, not just whatever is
    // audible: with absolute scheduling there may be a second of speech
    // queued on the audio thread, and barge-in that leaves it playing is
    // barge-in that does not work.
    for (const source of live.current) {
      source.onended = null
      try {
        source.stop()
      } catch {
        /* already finished */
      }
    }
    live.current.clear()
    nextAt.current = 0
    // Barge-in is immediate: they are talking NOW, so there is nothing to
    // wait for and the hangover would only keep a stale flag alive.
    clearHangover()
    speakingRef.current = false
    setSpeaking(false)
  }, [clearHangover])

  /** `start` needs `flush` for barge-in, and is defined before it. */
  const flushRef = useRef<() => void>(() => undefined)
  flushRef.current = flush

  const stop = useCallback(() => {
    flush()
    pending.current = []
    preroll.current = []
    vad.current = initialVad()
    stream.current?.getTracks().forEach((t) => {
      t.stop()
    })
    stream.current = null
    void capture.current?.close()
    capture.current = null
    inputAnalyser.current = null
    setState('off')
  }, [flush])

  const start = useCallback(async () => {
    if (capture.current) return
    setState('starting')
    try {
      const media = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })
      stream.current = media
      const ctx = new AudioContext({ sampleRate })
      capture.current = ctx

      const url = URL.createObjectURL(
        new Blob([WORKLET], { type: 'application/javascript' })
      )
      try {
        await ctx.audioWorklet.addModule(url)
      } finally {
        URL.revokeObjectURL(url)
      }

      const source = ctx.createMediaStreamSource(media)
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 256
      analyser.smoothingTimeConstant = 0.4
      inputAnalyser.current = analyser
      const node = new AudioWorkletNode(ctx, 'pcm-processor')
      node.port.onmessage = (event: MessageEvent<Float32Array>) => {
        if (mutedRef.current) return
        const frame = event.data
        if (mode.current === 'provider') {
          sink.current(bytesToBase64(float32ToPcm16(frame)))
          return
        }
        /**
         * WE OWN THE TURN. Frames are held until the detector says the
         * person has stopped, then the whole utterance goes up in one
         * message. Nothing is sent while they are silent, which is also
         * most of the bandwidth saved over streaming continuously.
         */
        // Always keep a little history, whatever the detector decides.
        preroll.current.push(frame)
        if (preroll.current.length > PREROLL_FRAMES) preroll.current.shift()

        const out = step(
          vad.current,
          rms(frame),
          Date.now(),
          undefined,
          speakingRef.current
        )
        vad.current = out.state
        if (out.event === 'speech-start') {
          // Seed with what was already said during the onset.
          pending.current = [...preroll.current]
          // Talking over the agent cuts it off: locally first, because a
          // round trip of hesitation is exactly what makes barge-in feel
          // broken, and the server is told separately.
          flushRef.current()
          speechStart.current?.()
          return
        }
        if (out.state.phase === 'speaking') {
          pending.current.push(frame)
          return
        }
        if (out.event === 'speech-end') {
          const frames = pending.current
          pending.current = []
          if (frames.length === 0) return
          const total = frames.reduce((n, f) => n + f.length, 0)
          const joined = new Float32Array(total)
          let at = 0
          for (const f of frames) {
            joined.set(f, at)
            at += f.length
          }
          utteranceSink.current?.(bytesToBase64(float32ToPcm16(joined)))
        }
      }
      source.connect(analyser)
      analyser.connect(node)
      // NOT connected to the destination: routing the microphone to the
      // speaker is how you get feedback howl on a laptop.
      setState('live')
    } catch (error) {
      console.error('[support voice] could not open the microphone', error)
      stop()
      setState('error')
    }
  }, [sampleRate, stop])

  const toggleMute = useCallback(() => {
    setMuted((was) => {
      const now = !was
      mutedRef.current = now
      // Silence what is already scheduled. With absolute scheduling there
      // can be a second or two of speech sitting on the audio thread, and
      // a mute that leaves it playing is not a mute.
      if (now) flush()
      return now
    })
  }, [flush])

  // A live microphone on an unmounted component is the one bug somebody
  // would notice from across a room.
  useEffect(() => stop, [stop])

  return {
    state,
    speaking,
    muted,
    inputAnalyser,
    outputAnalyser,
    start,
    stop,
    play,
    flush,
    toggleMute,
  }
}
